import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { AuthProvider } from "@/providers/AuthProvider";
import PrivateRoute from "@/components/PrivateRoute";
import PublicRoute from "@/components/PublicRoute";
import Landing from "./pages/Landing";
import RoleSelection from "./pages/RoleSelection";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import ProfileSettings from "./pages/ProfileSettings";
import Notifications from "./pages/Notifications";
import Messages from "./pages/Messages";
import Projects from "./pages/Projects";
import ProjectDetails from "./pages/ProjectDetails";
import PublicProfile from "./pages/PublicProfile";
import CreateProject from "./pages/CreateProject";
import ProjectMarketplace from "./pages/ProjectMarketplace";
import Wallet from "./pages/Wallet";
import Certificates from "./pages/Certificates";
import Batches from "./pages/Batches";
import Students from "./pages/Students";
import StudentProjects from "./pages/StudentProjects";
import VerifyCertificate from "./pages/VerifyCertificate";
import AdminLayout from "./components/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminPlatformSettings from "./pages/admin/AdminPlatformSettings";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminProjects from "./pages/admin/AdminProjects";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminEligibility from "./pages/admin/AdminEligibility";
import AdminQuizzes from "./pages/admin/AdminQuizzes";
import AdminReferrals from "./pages/admin/AdminReferrals";
import AdminDisputes from "./pages/admin/AdminDisputes";
import AdminReports from "./pages/admin/AdminReports";
import AdminGiftCards from "./pages/admin/AdminGiftCards";
import AdminWallets from "./pages/admin/AdminWallets";
import AdminCertificates from "./pages/admin/AdminCertificates";
import AdminSystemSettings from "./pages/admin/AdminSystemSettings";
import Leaderboard from "./pages/Leaderboard";
import Portfolio from "./pages/Portfolio";
import LearningResources from "./pages/LearningResources";
import SkillQuizzes from "./pages/SkillQuizzes";
import ActivityFeed from "./pages/ActivityFeed";
import Disputes from "./pages/Disputes";
import Partnerships from "./pages/Partnerships";
import CampusAnalytics from "./pages/CampusAnalytics";
import NotFound from "./pages/NotFound";
import Careers from "./pages/Careers";
import Partners from "./pages/Partners";
import Blog from "./pages/Blog";
import BlogPostDetails from "./pages/BlogPostDetails";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Contact from "./pages/Contact";
import Features from "./pages/Features";
import HowItWorks from "./pages/HowItWorks";
import AboutUs from "./pages/AboutUs";
import ExploreProjects from "./pages/ExploreProjects";
import ReferEarn from "./pages/ReferEarn";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={Capacitor.isNativePlatform() ? <Navigate to="/dashboard" replace /> : <Landing />} />
            
            {/* Public Auth Routes (Redirect to dashboard if logged in) */}
            <Route element={<PublicRoute />}>
              <Route path="/role-selection" element={<RoleSelection />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
            </Route>

            {/* Password Reset Route (Needs to be accessible when Supabase auto-logs in via recovery link) */}
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Public Footer Pages */}
            <Route path="/careers" element={<Careers />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPostDetails />} />
            <Route path="/blogs" element={<Navigate to="/blog" replace />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/features" element={<Features />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/explore-projects" element={<ExploreProjects />} />

            <Route path="/verify/:uid" element={<VerifyCertificate />} />
            
            {/* Protected Routes */}
            <Route element={<PrivateRoute />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/settings" element={<ProfileSettings />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/new" element={<CreateProject />} />
              <Route path="/projects/:id" element={<ProjectDetails />} />
              <Route path="/profile/:id" element={<PublicProfile />} />
              <Route path="/portfolio/:id" element={<Portfolio />} />
              <Route path="/marketplace" element={<ProjectMarketplace />} />
              <Route path="/refer" element={<ReferEarn />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/certificates" element={<Certificates />} />
              <Route path="/batches" element={<Batches />} />
              <Route path="/students" element={<Students />} />
              <Route path="/student-projects" element={<StudentProjects />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="/admin/overview" replace />} />
                <Route path="overview" element={<AdminOverview />} />
                <Route path="platform-settings" element={<AdminPlatformSettings />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="projects" element={<AdminProjects />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="eligibility" element={<AdminEligibility />} />
                <Route path="quizzes" element={<AdminQuizzes />} />
                <Route path="referrals" element={<AdminReferrals />} />
                <Route path="disputes" element={<AdminDisputes />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="gift-cards" element={<AdminGiftCards />} />
                <Route path="wallets" element={<AdminWallets />} />
                <Route path="certificates" element={<AdminCertificates />} />
                <Route path="system-settings" element={<AdminSystemSettings />} />
              </Route>
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/resources" element={<LearningResources />} />
              <Route path="/quizzes" element={<SkillQuizzes />} />
              <Route path="/activity" element={<ActivityFeed />} />
              <Route path="/disputes" element={<Disputes />} />
              <Route path="/partnerships" element={<Partnerships />} />
              <Route path="/campus-analytics" element={<CampusAnalytics />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
